import React from "react";
import { useParams } from "react-router-dom";
import { useSiteContent } from "../content/SiteContentContext.jsx";
import { safeUrl } from "../utils/safeUrl";
import { RichText } from "../utils/richText.jsx";
import { Blocks } from "../utils/blocks.jsx";

const normalizeString = (str) => {
  return str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-");
};

const EducativeOfferDetail = () => {
  const { courseName } = useParams();
  const { academicDegrees } = useSiteContent();

  const foundItem = (academicDegrees || []).find(
    (academicDegree) =>
      normalizeString(academicDegree.name) === normalizeString(courseName)
  );

  if (!foundItem) {
    return (
      <div className="w-full overflow-hidden footerAdjust">
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <h1 className="text-3xl mb-4">
            <strong>Oferta no encontrada</strong>
          </h1>
          <p>La oferta que buscas no está disponible o cambió de dirección.</p>
        </div>
      </div>
    );
  }

  // Defensivo: si el contenido publicado no trae estos objetos, no reventar la vista.
  const why = foundItem.why || { inline: "", highlights: [], reasons: [] };
  const admission = foundItem.admissionProfile || { inline: "", highlights: [] };
  const graduation = foundItem.graduationProfile || { inline: "", highlights: [] };
  const whyHl = Array.isArray(why.highlights) ? why.highlights : [];
  const whyReasons = Array.isArray(why.reasons) ? why.reasons : [];
  const admHl = Array.isArray(admission.highlights) ? admission.highlights : [];
  const grdHl = Array.isArray(graduation.highlights) ? graduation.highlights : [];

  return (
    <div className="w-full overflow-hidden">
      {/* ===== Hero ===== */}
      <section className="brand-gradient text-white relative overflow-hidden">
        <div className="blob-glow w-[460px] h-[460px] -top-44 -right-24" />
        <div className="mx-auto w-full max-w-[1100px] px-6 py-16 md:py-20 relative z-[1]">
          <span className="pill pill-glass">
            <i className="fa-solid fa-house-laptop" /> Presencial o en línea
          </span>

          <h1 className="mt-5 font-bold text-[34px] ss:text-[46px] leading-[1.08]">
            {foundItem.nameInPage}
          </h1>

          {foundItem.introductionToTheProgram ? (
            <RichText
              html={foundItem.introductionToTheProgram}
              className="mt-4 text-white/85 text-[18px] leading-[30px] max-w-[720px]"
            />
          ) : null}

          <div className="w-[104px] h-[5px] rounded bg-[#0165fc] mt-7" />

          <div className="flex flex-wrap gap-3 mt-7">
            {foundItem.type !== "Diplomado" ? (
              <div className="info-chip">
                <span className="chip-ico">
                  <i className="fa-solid fa-certificate" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="chip-label">RVOE</span>
                  <span className="chip-value">{foundItem.rvoe || "—"}</span>
                </span>
              </div>
            ) : null}

            {foundItem.duration ? (
              <div className="info-chip">
                <span className="chip-ico">
                  <i className="fa-regular fa-clock" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="chip-label">Duración</span>
                  <span className="chip-value">{foundItem.duration}</span>
                </span>
              </div>
            ) : null}

            {foundItem.modules ? (
              <div className="info-chip">
                <span className="chip-ico">
                  <i className="fa-solid fa-layer-group" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="chip-label">Plan de estudios</span>
                  <span className="chip-value">{foundItem.modules} módulos</span>
                </span>
              </div>
            ) : null}
          </div>

          {foundItem.rvoeLink ? (
            <a
              href={safeUrl(foundItem.rvoeLink)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-5 text-[#9db8ff] font-medium hover:text-white transition-colors"
            >
              <i className="fa-solid fa-up-right-from-square" /> Consultar RVOE oficial
            </a>
          ) : null}
        </div>
      </section>

      {/* ===== Video ===== */}
      {foundItem.videoLink ? (
        <section className="brand-soft py-14 px-6">
          <div className="video-frame">
            <iframe
              src={safeUrl(foundItem.videoLink)}
              title={`Video ${foundItem.nameInPage || "de la oferta"}`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </section>
      ) : null}

      {/* ===== Contenido ===== */}
      <section className="brand-soft pb-20 pt-6 px-6">
        <div className="mx-auto w-full max-w-[880px] flex flex-col gap-6">
          {foundItem.objetive !== "" ? (
            <div className="content-card fade-up">
              <div className="section-title mb-3">
                <span className="st-ico">
                  <i className="fa-solid fa-bullseye" />
                </span>
                Objetivo
              </div>
              <RichText html={foundItem.objetive} className="text-tPrimary leading-[28px]" />
            </div>
          ) : null}

          {foundItem.question || why.inline !== "" || (whyReasons.length === 0 && whyHl.length > 0) ? (
            <div className="content-card fade-up">
              {foundItem.question ? (
                <div className="section-title mb-3">
                  <span className="st-ico">
                    <i className="fa-solid fa-lightbulb" />
                  </span>
                  {foundItem.question}
                </div>
              ) : null}
              {why.inline !== "" && (
                <RichText html={why.inline} className="text-tPrimary leading-[28px]" />
              )}
              {whyReasons.length === 0 && whyHl.length > 0 && (
                <ul className="brand-list mt-3 text-tPrimary">
                  {whyHl.map((highlight, index) => (
                    <li key={index}>{highlight}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {whyReasons.length > 0 ? (
            <div className="grid grid-cols-1 ss:grid-cols-2 gap-6">
              {whyReasons.map((reason, index) => (
                <div
                  key={index}
                  className="content-card fade-up flex items-start gap-5 hover:-translate-y-1 transition-transform duration-300"
                  style={{ animationDelay: `${index * 90}ms` }}
                >
                  <div className="icon-orb w-[58px] h-[58px] flex-shrink-0 flex items-center justify-center">
                    <i className={`fa-solid fa-${reason.icon || "circle-check"} text-[22px]`} />
                  </div>
                  <div>
                    <h4 className="font-semibold italic text-tPrimary text-[20px] mb-1.5">
                      {reason.title}
                    </h4>
                    <p className="text-tPrimary text-[16.5px] leading-[25px]">{reason.content}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {admission.inline !== "" || admHl.length > 0 ? (
            <div className="content-card fade-up">
              <div className="section-title mb-3">
                <span className="st-ico">
                  <i className="fa-solid fa-user-check" />
                </span>
                Perfil del aspirante
              </div>
              {admission.inline !== "" && (
                <RichText html={admission.inline} className="text-tPrimary leading-[28px]" />
              )}
              {admHl.length > 0 && (
                <ul className="brand-list mt-3 text-tPrimary">
                  {admHl.map((highlight, index) => (
                    <li key={index}>{highlight}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {graduation.inline !== "" || grdHl.length > 0 ? (
            <div className="content-card fade-up">
              <div className="section-title mb-3">
                <span className="st-ico">
                  <i className="fa-solid fa-user-graduate" />
                </span>
                Perfil del egresado
              </div>
              {graduation.inline !== "" && (
                <RichText html={graduation.inline} className="text-tPrimary leading-[28px]" />
              )}
              {grdHl.length > 0 && (
                <ul className="brand-list mt-3 text-tPrimary">
                  {grdHl.map((highlight, index) => (
                    <li key={index}>{highlight}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          {Array.isArray(foundItem.sections) &&
            foundItem.sections.map((sec, index) => {
              const hasBlocks = Array.isArray(sec?.blocks) && sec.blocks.length > 0;
              if (!sec || !(sec.title || sec.body || hasBlocks)) return null;
              return (
                <div className="content-card fade-up" key={`sec-${index}`}>
                  {sec.title ? (
                    <div className="section-title mb-3">
                      <span className="st-ico">
                        <i className="fa-solid fa-circle-info" />
                      </span>
                      {sec.title}
                    </div>
                  ) : null}
                  {sec.body ? (
                    <RichText html={sec.body} className="text-tPrimary leading-[28px]" />
                  ) : null}
                  {hasBlocks ? (
                    <div className={sec.title || sec.body ? "mt-5" : ""}>
                      <Blocks blocks={sec.blocks} />
                    </div>
                  ) : null}
                </div>
              );
            })}
        </div>
      </section>
    </div>
  );
};

export default EducativeOfferDetail;
